import {
  Alert,
  Button,
  Chip,
  Divider,
  Input,
  Sheet,
  Typography,
} from "@mui/joy";
import { Box, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Season_Entity } from "../../entities/season";
import {
  formOpenSelector,
  selectedSeasonIDsSelector,
  setFormOpen,
  setSelectedSeasonIDs,
} from "../../reducers/rhapsody";
import {
  useCreateSeasonMutation,
  useDeleteSeasonMutation,
  useUpdateSeasonMutation,
} from "../../redux/season/seasonEndpoints";
import { useSeason } from "../../redux/season/seasonHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";
import DialogClose from "../dialogClose/dialogClose";
import { GooglePlaceSuggestions } from "../googlePlaceSuggestions/googlePlaceSuggestions";
import { usePro } from "../usePro/usePro";
import { NerdPhoneField } from "@nerdjs/nerd-ui";
import Upload from "../fileUpload/fileUpload";
import ColorPicker from "../colorPicker/colorPicker";
import {
  DialogHeader,
  FieldLabel,
  GroupCaption,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../formKit/formKit";

/**
 *
 * @returns {ReactElement} FormSeason page
 */
export function FormSeason() {
  const seasonIDs = useSelector(selectedSeasonIDsSelector);
  const { season } = useSeason(seasonIDs);
  const open = useSelector(formOpenSelector("season"));
  const [_season, setSeason] = useState<Partial<Season_Entity>>({});
  const pro = usePro();
  const [createSeason] = useCreateSeasonMutation();
  const [updateSeason] = useUpdateSeasonMutation();
  const [deleteSeason] = useDeleteSeasonMutation();

  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  useEffect(() => {
    if (season?.id) {
      setSeason(season);
    }
  }, [season]);

  const onClose = () => {
    setSeason({});
    dispatch(setSelectedSeasonIDs());
    dispatch(setFormOpen({ isOpen: false, formID: "season" }));
  };

  const save = () => {
    if (seasonIDs && _season) {
      updateSeason({ id: seasonIDs.id, body: _season });
    } else if (_season) {
      createSeason(_season);
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: <Typography>You are about to delete a Season.</Typography>,
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (seasonIDs?.id) deleteSeason(seasonIDs?.id);
    onClose();
  };

  return (
    <DialogClose
      open={open}
      onClose={onClose}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle>
        <DialogHeader
          title={`${seasonIDs?.id ? "Edit" : "Create"} Season`}
          subtitle={
            !seasonIDs?.id
              ? "A season groups your projects so every year stays tidy."
              : undefined
          }
        />
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: 500,
          maxWidth: "100%",
        }}
      >
        {!pro ? (
          <Alert
            color="info"
            startDecorator={<i className="fa-regular fa-circle-info"></i>}
          >
            Add Seasons you play outside of Rhapsody allowing you to manage all
            your work seamlessly in one platform.
          </Alert>
        ) : (
          []
        )}
        <GroupCaption optional>Cover artwork</GroupCaption>
        <Sheet variant="soft" sx={{ ...groupSheetSx, p: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {_season.coverURL ? (
              <img
                src={_season.coverURL}
                style={{ width: 95, height: 95, borderRadius: "8px" }}
              />
            ) : (
              <Upload
                onUpload={(f) => setSeason((t) => ({ ...t, coverURL: f }))}
                cropDimension={{ width: 250, height: 250 }}
              >
                <Box
                  sx={{
                    width: 95,
                    height: 95,
                    flexShrink: 0,
                    border: "dashed 1.5px #9e9e9e",
                    borderRadius: "8px",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography level="h4" sx={{ color: "#9e9e9e" }}>
                    <i className="fa-duotone fa-image"></i>
                  </Typography>
                </Box>
              </Upload>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                level="body2"
                sx={{ color: "black", fontWeight: 600 }}
              >
                Season Cover
              </Typography>
              <Typography level="body3">
                An artwork respresenting the Season. It must be at least 250px
                by 250px with a max size of 512KB. The supported MIME types are
                JPG, PNG, and GIf.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Upload
                  cropDimension={{ width: 250, height: 250 }}
                  onUpload={(f) => setSeason((t) => ({ ...t, coverURL: f }))}
                />
                {_season.coverURL ? (
                  <Chip
                    onClick={() => setSeason((t) => ({ ...t, coverURL: "" }))}
                    size="sm"
                    variant="outlined"
                    color="danger"
                  >
                    Remove Cover
                  </Chip>
                ) : (
                  []
                )}
              </Box>
            </Box>
          </Box>
        </Sheet>
        <GroupCaption>The basics</GroupCaption>
        <Sheet variant="soft" sx={groupSheetSx}>
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            autoFocus
            placeholder="e.g. 2026-2027 Season"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-signature"
                color={tileColors.blue}
                label="Name"
              />
            }
            value={_season.name}
            onChange={(e) => setSeason((v) => ({ ...v, name: e.target.value }))}
            type="text"
          />
          <Divider />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pl: 1.5,
              pr: 1,
              py: 1,
            }}
          >
            <FieldLabel
              icon="fa-solid fa-palette"
              color={tileColors.purple}
              label="Color"
            />
            <ColorPicker
              onChange={(e) => setSeason((v) => ({ ...v, color: e }))}
              color={_season.color ?? "#000000"}
            />
          </Box>
        </Sheet>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {seasonIDs?.id ? (
          <Button
            variant="soft"
            onClick={askDelete}
            color="danger"
            sx={pillButtonSx}
          >
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={onClose}
          color="neutral"
          variant="plain"
          sx={pillButtonSx}
        >
          Cancel
        </Button>
        <Button
          sx={{ ...pillButtonSx, px: 3 }}
          endDecorator={
            !pro ? (
              <Typography
                sx={{ color: "white" }}
                variant="outlined"
                color="neutral"
                level="body5"
              >
                PRO
              </Typography>
            ) : undefined
          }
          onClick={save}
          color="info"
        >
          {seasonIDs?.id ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </DialogClose>
  );
}
